import {
  ReportData,
  ReportInput,
  ReportHeroMetrics,
  ReportImpactBreakdown,
  ImpactBreakdownEntry,
  GenerationEntry,
  ReportEmissions,
  EmissionsEntry,
  ImpactMode,
} from './types';
import {
  getCategoryDisplayName,
  isHiddenCategory,
  interconnectionByCanonicalFuel,
  interconnectionDisplacementForFuel,
} from '../../model/domain/definitions';

const TECH_LABELS: Record<string, string> = {
  solar_ground: 'Ground-Mount Solar',
  solar_rooftop: 'Rooftop Solar',
  wind_onshore: 'Onshore Wind',
  wind_offshore: 'Offshore Wind',
};

interface RawImpactEntry {
  Impact_Category: string;
  Total_Impact?: number;
  USD: number;
}

export function getLifetimeMultiplier(lifetimeProjection: { lifetimeGenerationGwh: number; baseYearGenerationMwh: number | null | undefined } | null): number | null {
  if (!lifetimeProjection || !lifetimeProjection.baseYearGenerationMwh || lifetimeProjection.baseYearGenerationMwh <= 0) return null;
  return (lifetimeProjection.lifetimeGenerationGwh * 1000) / lifetimeProjection.baseYearGenerationMwh;
}

// Year-1 avoided CO2 (tonnes) for the selected scope, read from the SAME
// Emissions.air "Carbon" entry the emissions table (page 11) uses. Operational
// is the gross avoided; Cradle-to-Grave nets out the project's own
// infrastructure carbon — so ops > c2g by the manufacturing burden. Returns
// null when the run has no emissions block (then callers fall back to the
// C2G-derived impactTotals.co2eKg).
function carbonYear1Tonnes(
  modelResults: ReportInput['modelResults'],
  mode: ImpactMode,
): number | null {
  const analysisKey = mode === 'ctg' ? 'Cradle_to_Grave_Analysis' : 'Operational_Analysis';
  const air = modelResults.summary_json?.Emissions?.[analysisKey]?.air as
    | { Name?: string; Value?: number }[]
    | undefined;
  if (!Array.isArray(air)) return null;
  const carbon = air.find((e) => e.Name === 'Carbon');
  return carbon && typeof carbon.Value === 'number' ? carbon.Value : null;
}

// Scale the lifetime projection (built from the page's C2G per-MWh rates) to the
// selected report scope, so page 10 agrees with the scope-correct hero/exec
// summary. Lifetime is linear in the year-1 rate, so scaling every impact by the
// scope's year-1 / C2G year-1 ratio yields the true operations (or C2G) lifetime.
// C2G reports get ratio 1 (unchanged). CO2e is handled the same way but anchored
// on an authoritative target (scope year-1 carbon x lifetime multiplier) so page
// 10's lifetime CO2e lands EXACTLY on the page-11 emissions-table figure.
function scaleLifetimeToScope(
  proj: ReportInput['lifetimeProjection'],
  heroDalys: number,
  heroPdfs: number,
  heroUsd: number,
  base: ReportInput['impactTotals'],
  targetCo2eTonnes: number | null,
) {
  if (!proj) return proj;
  const r = (num: number, den: number) => (den !== 0 ? num / den : 1);
  const rD = r(heroDalys, base.dalys);
  const rP = r(heroPdfs, base.pdfs);
  const rU = r(heroUsd, base.usd);
  // rC drives page-10 lifetime CO2e onto the page-11 target. For a C2G report the
  // target equals the projection's own C2G total, so rC === 1 (unchanged).
  const rC =
    targetCo2eTonnes != null && proj.lifetimeAvoidedCo2eTonnes !== 0
      ? targetCo2eTonnes / proj.lifetimeAvoidedCo2eTonnes
      : 1;
  if (rD === 1 && rP === 1 && rU === 1 && rC === 1) return proj;
  return {
    ...proj,
    lifetimeAvoidedDalys: proj.lifetimeAvoidedDalys * rD,
    lifetimeAvoidedPdfs: proj.lifetimeAvoidedPdfs * rP,
    lifetimeEconomicNominalUsd: proj.lifetimeEconomicNominalUsd * rU,
    lifetimeEconomicNpvUsd: proj.lifetimeEconomicNpvUsd * rU,
    lifetimeAvoidedCo2eTonnes: proj.lifetimeAvoidedCo2eTonnes * rC,
    netLifetimeCo2eTonnes: proj.netLifetimeCo2eTonnes * rC,
    impactRates: {
      ...proj.impactRates,
      dalysPerMwh: proj.impactRates.dalysPerMwh * rD,
      pdfsPerMwh: proj.impactRates.pdfsPerMwh * rP,
      usdPerMwh: proj.impactRates.usdPerMwh * rU,
      co2eKgPerMwh: proj.impactRates.co2eKgPerMwh * rC,
    },
    yearlyProjections: proj.yearlyProjections.map((y) => ({
      ...y,
      avoidedDalys: y.avoidedDalys * rD,
      avoidedPdfs: y.avoidedPdfs * rP,
      economicValueNominalUsd: y.economicValueNominalUsd * rU,
      economicValueNpvUsd: y.economicValueNpvUsd * rU,
      avoidedCo2eKg: y.avoidedCo2eKg * rC,
    })),
  };
}

export function extractReportData(input: ReportInput): ReportData {
  const { modelResults, impactTotals, lifetimeProjection } = input;
  const mode = input.impactMode || 'ctg';

  const heroMetrics = extractHeroMetrics(input, mode);
  // Page-10 lifetime must match the scope-correct hero (page 2). heroMetrics
  // already carries the scope-correct year-1 totals. For CO2e, target page 10 on
  // the exact page-11 figure: scope year-1 avoided carbon x lifetime multiplier
  // (getLifetimeMultiplier reads only generation, which scaling never touches).
  const co2eMultiplier = getLifetimeMultiplier(lifetimeProjection);
  const scopeCarbonTonnes = carbonYear1Tonnes(modelResults, mode);
  const targetLifetimeCo2eTonnes =
    scopeCarbonTonnes != null && co2eMultiplier != null
      ? scopeCarbonTonnes * co2eMultiplier
      : null;
  const scopedLifetime = scaleLifetimeToScope(
    lifetimeProjection,
    heroMetrics.dalys,
    heroMetrics.pdfs,
    heroMetrics.usd,
    impactTotals,
    targetLifetimeCo2eTonnes,
  );
  const impactBreakdown = extractImpactBreakdown(modelResults, mode);
  const generation = extractGeneration(modelResults);
  const emissions = extractEmissions(modelResults, mode);

  // Determine profile source from model results generation_metadata (most reliable)
  const genMeta = modelResults.summary_json?.generation_metadata;
  let profileSource: string;
  if (genMeta?.profile_source === 'sam_generated') {
    profileSource = 'NREL SAM';
  } else if (genMeta?.profile_source === 'user_provided') {
    profileSource = 'Customer Supplied';
  } else {
    // Fallback: existing projects always have customer data; future without SAM flag = customer
    profileSource = input.existingProjectFlag ? 'Customer Supplied' : 'NREL SAM';
  }

  // Capacity factor from model results or compute from generation
  const capacityFactor = genMeta?.capacity_factor
    ?? (input.capacityMw > 0 ? heroMetrics.generationMwh / (input.capacityMw * 8760) : undefined);

  // Build location string: prefer county/state, fallback to state-only, then raw location
  let location = input.location;
  if (input.county && input.projectConfig?.state) {
    const countyStr = input.county.toLowerCase().includes("county") ? input.county : `${input.county} County`;
    location = `${countyStr}, ${input.projectConfig.state}`;
  } else if (input.projectConfig?.state) {
    const st = input.projectConfig.state;
    // If state field contains "County, ST" format already, use as-is;
    // if it contains "Name, ST" (comma-separated), add "County" to the name part
    if (st.includes(',')) {
      const [namePart, ...rest] = st.split(',').map(s => s.trim());
      if (namePart && !namePart.toLowerCase().includes('county')) {
        location = `${namePart} County, ${rest.join(', ')}`;
      } else {
        location = st;
      }
    } else {
      location = st;
    }
  }

  return {
    project: {
      projectName: input.projectName,
      techType: input.techType,
      techLabel: TECH_LABELS[input.techType] || input.techType.replace(/_/g, ' '),
      capacityMw: input.capacityMw,
      location,
      analysisYear: input.analysisYear,
      isFutureProject: input.isFutureProject,
      codDate: input.codDate,
      customerName: input.customerName,
      county: input.county,
      profileSource,
      capacityFactor,
    },
    projectConfig: input.projectConfig,
    heroMetrics,
    impactBreakdown,
    generation,
    emissions,
    lifetimeProjection: input.isFutureProject ? scopedLifetime : null,
    impactMode: mode,
    generatedAt: new Date().toISOString(),
    profileComparison: input.profileComparison,
    profileLabel: input.profileLabel,
    gridBaseline: input.gridBaseline,
  };
}

function extractHeroMetrics(input: ReportInput, mode: ImpactMode): ReportHeroMetrics {
  const { impactTotals, lifetimeProjection, isFutureProject, modelResults } = input;

  // The hero endpoint totals must match the chosen REPORT SCOPE, not the page's
  // ambient impact-mode. `impactTotals` reflects whatever the project page was
  // showing (C2G by default), so sourcing the hero from it made an Operations
  // report show C2G numbers while the breakdown pages switched — mixed numbers.
  // Pull DALYs/PDFs/USD from the scope-selected analysis block instead.
  const analysisKey = mode === 'ctg' ? 'Cradle_to_Grave_Analysis' : 'Operational_Analysis';
  const total = modelResults.summary_json?.Impacts?.[analysisKey]?.Total as
    | { DALYs?: number; PDFs?: number; USD?: number }
    | undefined;
  const dalys = total?.DALYs ?? impactTotals.dalys;
  const pdfs = total?.PDFs ?? impactTotals.pdfs;
  const usd = total?.USD ?? impactTotals.usd;

  // Year-1 avoided CO2e follows the report scope too: operational is the gross
  // avoided combustion carbon; C2G nets out the project's infrastructure carbon.
  // Read it from the same scope air "Carbon" entry the emissions table uses so
  // page 2 and page 11 agree; fall back to the C2G impactTotals when no
  // emissions block is present.
  const scopeCarbonTonnes = carbonYear1Tonnes(modelResults, mode);

  // Model already applies avoided framing (positive = benefit), no negation needed.
  const metrics: ReportHeroMetrics = {
    dalys,
    pdfs,
    usd,
    co2eKg: scopeCarbonTonnes != null ? scopeCarbonTonnes * 1000 : impactTotals.co2eKg,
    generationMwh: impactTotals.generationMwh,
  };

  if (isFutureProject && lifetimeProjection) {
    // The lifetime projection was built from the page-mode year-1 totals; scale
    // it by the scope's year-1 vs that base so the lifetime hero stays
    // consistent with the (now scope-correct) year-1 hero.
    const scale = (num: number, base: number) =>
      base !== 0 ? num / base : 1;
    metrics.lifetimeDalys =
      lifetimeProjection.lifetimeAvoidedDalys * scale(dalys, impactTotals.dalys);
    metrics.lifetimePdfs =
      lifetimeProjection.lifetimeAvoidedPdfs * scale(pdfs, impactTotals.pdfs);
    metrics.lifetimeUsd =
      lifetimeProjection.lifetimeEconomicNpvUsd * scale(usd, impactTotals.usd);
    metrics.lifetimeYears = lifetimeProjection.config.projectLifetimeYears;
  }

  return metrics;
}

function extractImpactBreakdown(modelResults: ReportInput['modelResults'], mode: ImpactMode): ReportImpactBreakdown {
  // Cradle_to_Grave_Analysis = full cradle-to-grave (with infrastructure).
  // Operational_Analysis = ops-only (cradle-to-grave minus infrastructure).
  const analysisKey = mode === 'ctg' ? 'Cradle_to_Grave_Analysis' : 'Operational_Analysis';
  const analysisData = modelResults.summary_json?.Impacts?.[analysisKey];
  if (!analysisData) {
    return { health: [], biodiversity: [], economic: [] };
  }

  const healthData = ((analysisData.Public_Health || []) as RawImpactEntry[])
    .filter(e => !isHiddenCategory(e.Impact_Category));
  const biodiversityData = ((analysisData.Biodiversity || []) as RawImpactEntry[])
    .filter(e => !isHiddenCategory(e.Impact_Category));

  // Combine economic data from all sections (same pattern as ImpactMetrics.tsx)
  const combinedEconomic: Record<string, { usd: number; value: number }> = {};
  let totalEconomicAbs = 0;

  const sections = ['Public_Health', 'Biodiversity', 'Economic'] as const;
  for (const section of sections) {
    const entries = ((analysisData[section] || []) as RawImpactEntry[])
      .filter(e => !isHiddenCategory(e.Impact_Category));
    for (const entry of entries) {
      if (entry.USD) {
        const cat = getCategoryDisplayName(entry.Impact_Category || 'Unknown');
        if (!combinedEconomic[cat]) combinedEconomic[cat] = { usd: 0, value: 0 };
        combinedEconomic[cat].usd += entry.USD;
        combinedEconomic[cat].value += entry.Total_Impact || 0;
        totalEconomicAbs += Math.abs(entry.USD);
      }
    }
  }

  // Model already applies avoided framing (positive = benefit), no negation needed.
  const economicEntries: ImpactBreakdownEntry[] = Object.entries(combinedEconomic)
    .map(([category, { usd, value }]) => ({
      category,
      value,
      usd,
      percentage: totalEconomicAbs !== 0 ? (Math.abs(usd) / totalEconomicAbs) * 100 : 0,
    }))
    .sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd));

  // Return ALL entries with percentage field — let charts and tables decide filtering
  const healthEntries = healthData
    .map(e => ({ category: getCategoryDisplayName(e.Impact_Category), value: e.Total_Impact || 0, usd: e.USD }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const totalHealthAbs = healthEntries.reduce((s, e) => s + Math.abs(e.value), 0);
  const healthWithPct = healthEntries.map(e => ({
    ...e,
    percentage: totalHealthAbs > 0 ? (Math.abs(e.value) / totalHealthAbs) * 100 : 0,
  }));

  const bioEntries = biodiversityData
    .map(e => ({ category: getCategoryDisplayName(e.Impact_Category), value: e.Total_Impact || 0, usd: e.USD }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
  const totalBioAbs = bioEntries.reduce((s, e) => s + Math.abs(e.value), 0);
  const bioWithPct = bioEntries.map(e => ({
    ...e,
    percentage: totalBioAbs > 0 ? (Math.abs(e.value) / totalBioAbs) * 100 : 0,
  }));

  return {
    health: healthWithPct,
    biodiversity: bioWithPct,
    economic: economicEntries,
  };
}

function extractGeneration(modelResults: ReportInput['modelResults']): GenerationEntry[] {
  const gen = modelResults.summary_json?.Generation;
  if (!gen) return [];

  // Portion of each fuel's displacement that lands in NEIGHBORING balancing
  // authorities. Absent breakdown → empty map → every fuel's Interconnection
  // resolves to 0 (rendered as an em-dash). Sign matches "Difference"
  // (negative = displaced generation).
  const byFuel = interconnectionByCanonicalFuel(
    modelResults.summary_json?.interconnection_breakdown,
  );

  return Object.entries(gen).map(([fuelType, values]) => {
    const merged: Record<string, number | string> = {
      ...(values as Record<string, number | string>),
      Interconnection: interconnectionDisplacementForFuel(fuelType, byFuel),
    };
    return {
      fuelType: fuelType.replace(/_/g, ' '),
      values: merged,
    };
  });
}

function extractEmissions(modelResults: ReportInput['modelResults'], mode: ImpactMode): ReportEmissions {
  // Cradle_to_Grave_Analysis = full cradle-to-grave (with infrastructure).
  // Operational_Analysis = ops-only (cradle-to-grave minus infrastructure).
  const analysisKey = mode === 'ctg' ? 'Cradle_to_Grave_Analysis' : 'Operational_Analysis';
  const emissionsData = modelResults.summary_json?.Emissions?.[analysisKey];
  if (!emissionsData) {
    return { air: [], water: [], soil: [], resources: [] };
  }

  const result: ReportEmissions = { air: [], water: [], soil: [], resources: [] };

  const categoryMap: Record<string, keyof ReportEmissions> = {
    air: 'air',
    Air: 'air',
    water: 'water',
    Water: 'water',
    soil: 'soil',
    Soil: 'soil',
    resources: 'resources',
    Resources: 'resources',
    resource: 'resources',
    Resource: 'resources',
  };

  for (const [cat, data] of Object.entries(emissionsData)) {
    const key = categoryMap[cat];
    if (!key) continue;

    if (Array.isArray(data)) {
      result[key] = data.map((entry: Record<string, unknown>) => {
        // Model already applies avoided framing (positive = avoided), no negation needed.
        const mapped: EmissionsEntry = {
          name: String(entry.Name || entry.name || ''),
          value: Number(entry.Value || entry.value || 0),
        };
        // Copy all other fields
        for (const [k, v] of Object.entries(entry)) {
          if (k !== 'Name' && k !== 'name' && k !== 'Value' && k !== 'value') {
            mapped[k] = typeof v === 'number' ? v : String(v ?? '');
          }
        }
        return mapped;
      });
    }
  }

  return result;
}
